'use client';

import React from 'react';

import { CloseIcon } from '@assets/icons';
import type { TourPackageDetailViewModel } from '@api/index';
import { CashDiscountType } from '@api/index';
import CustomIcon from '@components/utils/CustomIcon';
import { getCurrencySymbol } from '@utils/utils';

import { formatCashDiscountBadgeText } from '../../detail/components/package-template/discountHelpers';
import { hasPriceTableData } from './hasPriceTableData';
import {
  CloseButton,
  DiscountBadge,
  DiscountText,
  Header,
  Modal,
  ModalContent,
  Overlay,
  PackageName,
  PackageSection,
  PriceCard,
  PriceCardsContainer,
  PriceInfo,
  PriceLabel,
  PriceNote,
  PriceOriginal,
  PriceTitle,
  PriceValue,
  Separator,
  Title,
} from './styled';

interface PriceTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  packageData?: TourPackageDetailViewModel;
}

const PriceTableModal: React.FC<PriceTableModalProps> = ({
  isOpen,
  onClose,
  packageData,
}) => {
  if (!isOpen || !packageData || !hasPriceTableData(packageData)) return null;

  const { hotelBoardTypeName, currencyCode, defaultPackageDetail } =
    packageData;
  const roomPrices = defaultPackageDetail?.roomPrices;
  const prices = (roomPrices?.prices || [])
    .filter((p) => p.price != null && p.price > 0)
    .sort((a, b) => {
      const order = (personCount?: number | null) => {
        if (personCount === 2) return 1;
        if (personCount === 1) return 2;
        if (personCount === 3) return 3;
        return 99;
      };

      return order(a.personCount) - order(b.personCount);
    });

  const childPrices = (roomPrices?.childPrices || []).filter(
    (p) => p.price != null && p.price > 0
  );
  const currency = getCurrencySymbol(currencyCode);

  const cashDiscountVariation =
    defaultPackageDetail?.detailedVariations?.find(
      (variation) => variation.cashDiscountType
    ) ||
    defaultPackageDetail?.detailedVariations?.find(
      (variation) => variation.isDefault
    );

  const isPerRoomDiscount =
    cashDiscountVariation?.cashDiscountType === CashDiscountType.PER_ROOM;

  const formatPrice = (price?: number) => {
    if (price == null || Number.isNaN(price)) return '0,00';
    return price.toFixed(2).replace('.', ',');
  };

  const calculateDiscount = (original?: number, discounted?: number) => {
    if (original == null || discounted == null) return null;
    if (original <= discounted) return null;
    return (original - discounted).toFixed(0);
  };

  const getRoomLabel = (personCount: number): string => {
    switch (personCount) {
      case 1:
        return 'Tek Kişilik Oda';
      case 2:
        return 'İki Kişilik Oda';
      case 3:
        return 'Üç Kişilik Oda';
      case 4:
        return 'Dört Kişilik Oda';
      case 5:
        return 'Beş Kişilik Oda';
      default:
        return `${personCount} Kişilik Oda`;
    }
  };

  const maxDiscount = prices.reduce((max, price) => {
    const discount = calculateDiscount(price.price, price.discountedPrice);
    return discount && parseFloat(discount) > parseFloat(max || '0')
      ? discount
      : max;
  }, null as string | null);
  const discountBadgeText =
    formatCashDiscountBadgeText(
      {
        cashDiscountRateAmountType:
          cashDiscountVariation?.cashDiscountRateAmountType,
        cashDiscountRateAmountValue:
          cashDiscountVariation?.cashDiscountRateAmountValue,
        originalCurrency:
          cashDiscountVariation?.originalCurrency || currencyCode,
      },
      currencyCode
    ) || (maxDiscount ? `${maxDiscount} ${currency} İndirim` : null);

  return (
    <Overlay onClick={onClose}>
      <Modal
        role='dialog'
        aria-modal='true'
        aria-labelledby='tour-price-table-title'
        onClick={(e) => e.stopPropagation()}
      >
        <ModalContent>
          <Header>
            <Title id='tour-price-table-title'>Fiyat Tablosu</Title>
            <CloseButton aria-label='Fiyat tablosunu kapat' onClick={onClose}>
              <CustomIcon
                width={24}
                height={24}
                paletteColor='grey1100'
                icon={CloseIcon}
              />
            </CloseButton>
          </Header>

          <PackageSection>
            <PackageName>{hotelBoardTypeName || 'Standart Paket'}</PackageName>
            {discountBadgeText ? (
              <DiscountBadge>
                <DiscountText>{discountBadgeText}</DiscountText>
              </DiscountBadge>
            ) : null}
          </PackageSection>

          <PriceCardsContainer>
            {prices.map((priceItem, index) => {
              const hasDiscount =
                priceItem.discountedPrice != null &&
                priceItem.price != null &&
                priceItem.discountedPrice < priceItem.price;
              const displayPrice = hasDiscount
                ? priceItem.discountedPrice
                : priceItem.price;

              return (
                <React.Fragment key={priceItem.personCount}>
                  <PriceCard>
                    <PriceInfo>
                      <PriceTitle>
                        {getRoomLabel(priceItem.personCount)}
                      </PriceTitle>
                      {hasDiscount ? (
                        <PriceOriginal>
                          {formatPrice(priceItem.price)} {currency}
                        </PriceOriginal>
                      ) : null}
                      <PriceValue>
                        {formatPrice(displayPrice)} {currency}
                      </PriceValue>
                    </PriceInfo>
                    <PriceNote>Kişi başı fiyat</PriceNote>
                  </PriceCard>
                  {index < prices.length + childPrices.length - 1 ? (
                    <Separator />
                  ) : null}
                </React.Fragment>
              );
            })}

            {childPrices.map((childPrice, index) => {
              const showChildDiscount =
                !isPerRoomDiscount &&
                childPrice.discountedPrice != null &&
                childPrice.discountedPrice < childPrice.price;
              const displayPrice = showChildDiscount
                ? childPrice.discountedPrice
                : childPrice.price;

              return (
                <React.Fragment key={`child-${childPrice.childOrder}-${index}`}>
                  <PriceCard>
                    <PriceInfo>
                      <PriceTitle>{childPrice.childOrder}. Çocuk</PriceTitle>
                      {showChildDiscount ? (
                        <PriceOriginal>
                          {formatPrice(childPrice.price)} {currency}
                        </PriceOriginal>
                      ) : null}
                      <PriceValue>
                        {formatPrice(displayPrice)} {currency}
                      </PriceValue>
                    </PriceInfo>
                    <PriceLabel>
                      ({childPrice.startAge}-{childPrice.endAge} Yaş)
                    </PriceLabel>
                  </PriceCard>
                  {index < childPrices.length - 1 ? <Separator /> : null}
                </React.Fragment>
              );
            })}
          </PriceCardsContainer>
        </ModalContent>
      </Modal>
    </Overlay>
  );
};

export default PriceTableModal;
